import { createTheme, ThemeOptions } from '@mui/material/styles'
import { enUS, Localization } from '@mui/material/locale'

import breakpoints from './breakpoints'
import componentsOverride from './overrides'
import palette from './palette'
import shadows from './shadows'
import createCustomShadows from './customShadows'
import typography from './typography'
import GlobalStyles from './globalStyles'
import spacing from './spacing'

/**
 * Types
 */

interface CustomShadowOptions {
  z1: string
  z1Up: string
  z4: string
  z4Up: string
  z8: string
  z8Up: string
  z12: string
  z16: string
  z20: string
  z24: string
  //
  primary: string
  secondary: string
  info: string
  success: string
  warning: string
  error: string
  //
  card: string
  dialog: string
  dropdown: string
}

declare module '@mui/material/styles' {
  interface Theme {
    customShadows: CustomShadowOptions
  }
  interface ThemeOptions {
    customShadows?: CustomShadowOptions
  }
}

interface GradientsPaletteOptions {
  primary: string
  info: string
  success: string
  warning: string
  error: string
}

interface ChartPaletteOptions {
  violet: string[]
  blue: string[]
  green: string[]
  yellow: string[]
  red: string[]
  orange: string[]
  pink: string[]
  teal: string[]
  cyan: string[]
  purple: string[]
  brown: string[]
  lime: string[]
  mint: string[]
  peach: string[]
  charcoal: string[]
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string
    dark: string
  }
  interface SimplePaletteColorOptions {
    lighter: string
    darker: string
  }
  interface PaletteColor {
    lighter: string
    darker: string
  }
  interface Palette {
    gradients: GradientsPaletteOptions
    chart: ChartPaletteOptions
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions
    chart: ChartPaletteOptions
  }
}

declare module '@mui/material' {
  interface Color {
    0: string
    500_8: string
    500_12: string
    500_16: string
    500_24: string
    500_32: string
    500_48: string
    500_56: string
    500_80: string
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsVariantOverrides {
    soft: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true
  }
}

declare module '@mui/material/Fab' {
  interface FabPropsVariantOverrides {
    outlined: true
    outlinedExtended: true
    soft: true
    softExtended: true
  }
}

declare module '@mui/material/Pagination' {
  interface PaginationPropsVariantOverrides {
    soft: true
  }

  interface PaginationPropsColorOverrides {
    info: true
    success: true
    warning: true
    error: true
  }
}

/**
 * Provider
 */

export function getTheme(locale: Localization = enUS, mode: 'light' | 'dark' = 'light') {
  const isLight = mode === 'light'
  const themeOptions: ThemeOptions = {
    palette: isLight ? palette.light : palette.dark,
    typography,
    breakpoints,
    shape: { borderRadius: 8 },
    direction: 'ltr',
    shadows: isLight ? shadows.light : shadows.dark,
    customShadows: createCustomShadows(mode),
  }

  const theme = createTheme(themeOptions, locale)

  theme.components = componentsOverride(theme)
  return theme
}

export { GlobalStyles }
